/**
 * Mock fixtures for the Signal Performance view (frontend-only).
 *
 * Model: a signal has current positions. Each position is one suggestion/trade
 * idea. Equity positions usually contain one equity leg; option positions can
 * contain multiple contract legs (spreads, condors, baskets).
 *
 * Nothing here talks to our backend. Price charts fetch REAL candles from the
 * existing /api/polygon/aggregates route. When the backend lands, replace
 * `getMockSignalPerformance` with a real fetch; props are 1:1 with these types.
 */

export type ProductType = "signal" | "scanner" | "alert";
export type AssetKind = "equity" | "option";
export type Direction = "long" | "short";
export type PositionStatus = "open" | "closed";
export type ExitReason = "hit_target" | "stopped" | "time_exit";
export type LegAction = "buy" | "sell";
/**
 * Where a contract's price came from on the last reprice. OPTIONS ONLY.
 * - "quote": NBBO mid (bid+ask)/2 — the accurate default
 * - "trade": last trade aggregate (legacy / liquid contracts)
 * - "daily": day close used when the live quote/trade was unavailable
 * - "quote_wide": NBBO mid but the spread was too wide to fully trust
 *                 (low-confidence estimate; shown so a leg is never frozen)
 * - "stale": every source failed; price is the last known good, flagged
 */
export type PriceSource = "quote" | "trade" | "daily" | "quote_wide" | "stale";

/** Exit rules a leg can carry. All optional — a leg may use any combination. */
export interface ExitRule {
  take_profit?: number; // price (contract price for options)
  stop_loss?: number; // price
  time_exit_days?: number; // close after N trading days
  /** Trailing stop as a fraction (0.15 = 15% below the running peak). */
  trail_pct?: number;
}

export interface TradeLeg {
  kind: AssetKind;
  ticker: string;
  underlying_ticker?: string;
  action?: LegAction;
  quantity?: number;
  direction?: Direction;
  strike?: number;
  expiry?: string; // YYYY-MM-DD
  right?: "call" | "put";
  polygon_ticker?: string; // O:... derived by backend
  entry: number;
  current_price: number;
  bid?: number;
  ask?: number;
  volume?: number;
  open_interest?: number;
  exit_price?: number;
  // ── OPTIONS-ONLY accurate-pricing fields (additive; absent on equities) ──
  /** ISO minute the entry mark was captured (NBBO quote mid at register). */
  entry_at?: string;
  /** NBBO bid/ask snapped at entry (audit trail for the entry mark). */
  entry_quote_bid?: number;
  entry_quote_ask?: number;
  /** ISO timestamp current_price was last refreshed by the closer. */
  priced_at?: string;
  /** Source of current_price on the last reprice. */
  price_source?: PriceSource;
  /** True when every price source failed and current_price is last-known-good. */
  is_stale?: boolean;
}

export interface TradePosition {
  id: string;
  name: string;
  kind: AssetKind;
  ticker: string;
  underlying_ticker: string;
  direction: Direction;
  strategy_type?: string;
  /** Net-credit option strategy (short premium): entry = credit received,
   *  current = cost to close. Set by the signals library. */
  is_credit?: boolean;
  expiry?: string;
  days_to_expiry?: number;
  entry: number;
  current_price: number;
  pnl_pct: number;
  /** Best price seen since open (used to compute the live trailing stop). */
  peak_price?: number;
  max_risk?: number;
  max_reward?: number;
  breakeven?: number;
  exit: ExitRule;
  legs: TradeLeg[];
  /**
   * Per-trade "why it qualified" facts — flat {column: value}. Each key
   * renders as its own column. Free-form: different signals use different keys.
   */
  criteria?: Record<string, string>;
  /** ISO timestamp of when this position was added to the book. */
  opened_at?: string;
  /** Dollar notional deployed into this position (confidence × base size). */
  notional_usd?: number;
  // ── Run grouping + pricing freshness (OPTIONS-ONLY; absent on equities) ──
  /**
   * Shared stamp for every position registered in the SAME run. Used to group
   * "Current Signal(s)" by run instead of exact-microsecond opened_at (which
   * differs per position and breaks grouping). Prefer batch_id; registered_at
   * is the minute-bucket fallback.
   */
  batch_id?: string;
  registered_at?: string;
  /** ISO timestamp this position's marks were last refreshed. */
  priced_at?: string;
  /** True when any leg's price is stale (every source failed). */
  is_stale?: boolean;
  /** True when an option leg is below the liquidity guard (OI < 50) so the
   *  position is not realistically fillable at the quoted mid. Shown for
   *  transparency with a warning banner; new signals are blocked from this. */
  illiquid?: boolean;
  /** Open interest of the worst (thinnest) leg, when illiquid. */
  worst_leg_oi?: number;
  /** Plain-English reason the position is flagged illiquid. */
  illiquid_reason?: string;
}

export interface CurrentPosition {
  kind: AssetKind;
  fired_at: string; // ISO
  status: PositionStatus;
  /** One-line summary (chips, cards). */
  thesis: string;
  /** Long-form markdown explanation of why this trade was selected. */
  thesis_md?: string;
  /**
   * Gemini-generated, beginner-proof broker steps (markdown). Written by the
   * signals library on the same event as thesis_md. When present, the
   * "How to trade this" section renders this instead of the locally-derived
   * brokerSteps() fallback.
   */
  how_to_trade_md?: string;
  confidence: "low" | "medium" | "high";
  positions: TradePosition[];
}

export interface SignalPerformanceSummary {
  all_time_pct: number;
  all_time_usd_on_10k: number;
  /** Total capital put to work across all trades (sum of per-trade notionals). */
  deployed_usd?: number;
  /** Whole-book PnL in dollars: realized (closed) + unrealized (open marks). */
  total_pnl_usd?: number;
  realized_pnl_usd?: number;
  unrealized_pnl_usd?: number;
  pct_7d: number;
  pct_30d: number;
  win_rate: number; // 0..1
  total_trades: number;
  closed_trades?: number;
  open_trades?: number;
  avg_win_pct: number;
  avg_loss_pct: number;
  max_drawdown_pct: number;
}

export interface EquityPoint {
  date: string; // YYYY-MM-DD
  value: number;
}

export interface PreviousTrade {
  id?: string;
  kind: AssetKind;
  ticker: string;
  underlying_ticker?: string;
  name?: string;
  strategy_type?: string;
  /** Net-credit option strategy: entry = credit received, exit = cost to close. */
  is_credit?: boolean;
  direction: Direction;
  strike?: number;
  expiry?: string;
  right?: "call" | "put";
  polygon_ticker?: string;
  entry: number;
  exit_price: number;
  exit_rule: ExitRule;
  exit_reason: ExitReason;
  pnl_pct: number;
  pnl_usd: number;
  notional_usd?: number;
  seeded_from_backtest?: boolean;
  fired_at: string; // ISO
  closed_at: string; // ISO
  legs?: TradeLeg[];
  /** Per-trade "why it qualified" facts — flat {column: value}, frozen at close. */
  criteria?: Record<string, string>;
}

export interface SignalPerformance {
  alert_id: string;
  product_type: ProductType;
  name: string;
  performance: SignalPerformanceSummary;
  equity_curve: EquityPoint[];
  current: CurrentPosition;
  previous_trades: PreviousTrade[];
}

// ── Helpers ────────────────────────────────────────────────────────────────

function buildEquityCurve(
  start: number,
  endPct: number,
  startDate: string,
  points: number,
): EquityPoint[] {
  const out: EquityPoint[] = [];
  const end = start * (1 + endPct / 100);
  const base = new Date(startDate).getTime();
  const dayMs = 86_400_000;
  for (let i = 0; i < points; i++) {
    const t = i / (points - 1);
    const noise = Math.sin(i * 1.7) * (start * 0.012);
    const value = Math.round(start + (end - start) * t + noise);
    out.push({
      date: new Date(base + i * dayMs * 4).toISOString().slice(0, 10),
      value,
    });
  }
  return out;
}

// ── EQUITY signal (primary focus) ────────────────────────────────────────────

const EQUITY_SIGNAL: SignalPerformance = {
  alert_id: "alert-b2e2d4928915",
  product_type: "signal",
  name: "Smart Money Confluence",
  performance: {
    all_time_pct: 28.4,
    all_time_usd_on_10k: 2840,
    pct_7d: 3.1,
    pct_30d: 11.2,
    win_rate: 0.64,
    total_trades: 42,
    avg_win_pct: 9.8,
    avg_loss_pct: -4.2,
    max_drawdown_pct: -12.5,
  },
  equity_curve: buildEquityCurve(10000, 28.4, "2026-01-02", 40),
  current: {
    kind: "equity",
    fired_at: "2026-05-12T13:30:00Z",
    status: "open",
    thesis:
      "Dark-pool accumulation + positive gamma flip into a clean breakout above prior supply.",
    thesis_md: `Institutional money has been quietly building a position in **Micron (MU)** for the past two weeks, and the options market just flipped in its favor. This setup combines three independent confirmations — which is exactly what this signal waits for before firing.

### What triggered the signal

* **Dark-pool accumulation** — over the last 9 sessions, $84M of off-exchange prints hit at or above the mid, with the dark-pool buy ratio climbing from 38% to 61%. Sustained prints above 55% historically precede a directional move within 10 trading days.
* **Gamma flip** — dealers crossed from net-short to net-long gamma at the $88 strike. Above this level, market-maker hedging now *dampens* sell-offs and *accelerates* rallies, giving the trade a structural tailwind.
* **Breakout above prior supply** — price reclaimed the $88.50 shelf that rejected three previous rallies since February. That level now acts as support, and the first retest held on declining volume.

### Why these levels

* **Entry $89.20** — the first clean retest after the breakout, keeping risk tight against structure rather than chasing the move.
* **Stop $82.00** — below both the gamma-flip strike and the 50-day moving average. If price returns here, the entire thesis is invalidated and the position should not be argued with.
* **Target $104.00** — the measured move from the consolidation range, sitting just below the $105 call wall where dealer hedging is likely to slow the advance.

### What would change our mind

If dark-pool buy ratio drops back below 45% for three consecutive sessions, or dealers flip back to net-short gamma below $88, the edge is gone — the time-exit at 25 trading days caps exposure either way.`,
    how_to_trade_md: `You only need to place the order once. After that, we watch it for you and email you the moment anything closes.

* **Buy MU** near **$89.20**. This signal sizes it at $10,000.
* Set a **limit sell (take profit)** at **$104.00**.
* Set a **stop loss** at **$82.00**.
* If neither triggers, close the position **25 trading days** after entry.

That's it. When the target, stop, or time exit fires, you get an email so you know exactly when to sell.`,
    confidence: "high",
    positions: [
      {
        id: "eq-mu-long",
        name: "MU Long Breakout",
        kind: "equity",
        ticker: "MU",
        underlying_ticker: "MU",
        direction: "long",
        entry: 89.2,
        current_price: 93.4,
        pnl_pct: 4.7,
        criteria: {
          "Dark-pool buy ratio": "61%",
          "Net gamma": "$142M",
          "Price": "$93.40",
          "Breakout level": "$88.50",
          "Market cap": "$98B",
        },
        exit: { take_profit: 104.0, stop_loss: 82.0, time_exit_days: 25 },
        legs: [
          {
            kind: "equity",
            ticker: "MU",
            direction: "long",
            quantity: 1,
            entry: 89.2,
            current_price: 93.4,
          },
        ],
      },
      {
        id: "eq-nvda-long",
        name: "NVDA Momentum Add",
        kind: "equity",
        ticker: "NVDA",
        underlying_ticker: "NVDA",
        direction: "long",
        entry: 118.5,
        current_price: 122.1,
        pnl_pct: 3.0,
        criteria: {
          "Dark-pool buy ratio": "58%",
          "Net gamma": "$310M",
          "Price": "$122.10",
          "Breakout level": "$117.00",
          "Market cap": "$2.9T",
        },
        exit: { take_profit: 140.0, stop_loss: 110.0, time_exit_days: 25 },
        legs: [
          {
            kind: "equity",
            ticker: "NVDA",
            direction: "long",
            quantity: 1,
            entry: 118.5,
            current_price: 122.1,
          },
        ],
      },
      {
        id: "eq-amd-short",
        name: "AMD Relative Weakness Short",
        kind: "equity",
        ticker: "AMD",
        underlying_ticker: "AMD",
        direction: "short",
        entry: 165.0,
        current_price: 160.2, // profitable short
        pnl_pct: 2.9,
        criteria: {
          "Dark-pool buy ratio": "34%",
          "Net gamma": "-$95M",
          "Price": "$160.20",
          "Breakdown level": "$170.00",
          "Market cap": "$260B",
        },
        exit: { take_profit: 145.0, stop_loss: 175.0, time_exit_days: 25 },
        legs: [
          {
            kind: "equity",
            ticker: "AMD",
            direction: "short",
            quantity: 1,
            entry: 165.0,
            current_price: 160.2,
          },
        ],
      }
    ],
  },
  previous_trades: [
    {
      kind: "equity",
      ticker: "NVDA",
      direction: "long",
      entry: 118.0,
      exit_price: 134.6,
      exit_rule: { take_profit: 135.0, stop_loss: 110.0, time_exit_days: 25 },
      exit_reason: "hit_target",
      pnl_pct: 14.1,
      pnl_usd: 1410,
      fired_at: "2026-03-10T13:30:00Z",
      closed_at: "2026-03-28T20:00:00Z",
      criteria: {
        "Dark-pool buy ratio": "63%",
        "Net gamma": "$280M",
        "Entry price": "$116.20",
        "Breakout level": "$115.00",
        "Market cap": "$2.8T",
      },
    },
    {
      kind: "equity",
      ticker: "AMD",
      direction: "long",
      entry: 168.0,
      exit_price: 157.4,
      exit_rule: { take_profit: 190.0, stop_loss: 158.0, time_exit_days: 25 },
      exit_reason: "stopped",
      pnl_pct: -6.3,
      pnl_usd: -630,
      fired_at: "2026-02-18T13:30:00Z",
      closed_at: "2026-03-02T20:00:00Z",
      criteria: {
        "Dark-pool buy ratio": "56%",
        "Net gamma": "$88M",
        "Entry price": "$167.40",
        "Breakout level": "$165.00",
        "Market cap": "$255B",
      },
    },
    {
      kind: "equity",
      ticker: "TSLA",
      direction: "long",
      entry: 242.0,
      exit_price: 276.5,
      exit_rule: { take_profit: 278.0, stop_loss: 225.0, time_exit_days: 25 },
      exit_reason: "hit_target",
      pnl_pct: 14.3,
      pnl_usd: 1430,
      fired_at: "2026-01-21T13:30:00Z",
      closed_at: "2026-02-11T20:00:00Z",
      criteria: {
        "Dark-pool buy ratio": "67%",
        "Net gamma": "$520M",
        "Entry price": "$242.10",
        "Breakout level": "$240.00",
        "Market cap": "$780B",
      },
    },
  ],
};

// ── OPTION signal (multi-leg basket — polished next phase) ────────────────────

const OPTION_SIGNAL: SignalPerformance = {
  alert_id: "alert-9ef6f0026ff7",
  product_type: "signal",
  name: "Cheap Options Scanner",
  performance: {
    all_time_pct: 61.0,
    all_time_usd_on_10k: 6100,
    pct_7d: -2.4,
    pct_30d: 22.7,
    win_rate: 0.47,
    total_trades: 31,
    avg_win_pct: 142.0,
    avg_loss_pct: -58.0,
    max_drawdown_pct: -34.0,
  },
  equity_curve: buildEquityCurve(10000, 61.0, "2026-01-02", 40),
  current: {
    kind: "option",
    fired_at: "2026-05-14T13:30:00Z",
    status: "open",
    thesis:
      "Cheap OTM calls into an unusual-volume spike; small risk, asymmetric payoff on a gamma squeeze.",
    thesis_md: `This basket targets **cheap, out-of-the-money calls** on three names that all printed unusual options volume in the same session — the signature this scanner hunts for. The premise is simple: risk a small, defined premium for an asymmetric payoff if any one of the three squeezes.

### What triggered the signal

* **MU $190C** — call volume ran 6.4× the 20-day average with 71% of prints hitting the ask. Open interest at the strike doubled overnight, suggesting positioning ahead of a catalyst rather than hedging.
* **SOFI $14C** — a single sweep of 18,000 contracts crossed multiple exchanges in under a second. Sweeps of this size and urgency historically resolve directionally within two weeks.
* **PLTR $95C** — sustained ask-side buying every session this week, paired with the stock holding its breakout level on shrinking float turnover.

### Why a basket, not one trade

Cheap OTM calls are lottery tickets individually — roughly half expire worthless even in this scanner's history (win rate 47%). The edge comes from the **payoff asymmetry**: the average winner returns +142% while the average loser costs -58% of a much smaller premium. Spreading across three uncorrelated setups smooths the variance.

### Exit discipline

* Each leg carries its own **take-profit and stop on the contract price** — not the underlying.
* The 3-day time exit is deliberate: these are momentum trades. If the squeeze hasn't started within three sessions, theta decay starts winning and the position is cut regardless of conviction.`,
    confidence: "medium",
    positions: [
      {
        id: "opt-mu-long-call",
        name: "MU 190C Momentum Call",
        kind: "option",
        ticker: "MU",
        underlying_ticker: "MU",
        direction: "long",
        strategy_type: "Long Call",
        expiry: "2026-06-19",
        days_to_expiry: 22,
        entry: 0.19,
        current_price: 0.34,
        pnl_pct: 78.9,
        batch_id: "run-2026-05-14T1330",
        registered_at: "2026-05-14T13:30:00Z",
        priced_at: "2026-05-15T20:00:00Z",
        is_stale: false,
        max_risk: 19,
        max_reward: undefined,
        breakeven: 190.19,
        criteria: {
          "Call volume": "18,420",
          "Vol vs 20d avg": "6.4x",
          "Ask-side %": "71%",
          "OI change": "+104%",
          "IV rank": "42",
          "DTE": "22",
        },
        exit: { take_profit: 1.1, stop_loss: 0.1, time_exit_days: 3 },
        legs: [
          {
            kind: "option",
            ticker: "MU",
            underlying_ticker: "MU",
            action: "buy",
            quantity: 1,
            direction: "long",
            strike: 190,
            expiry: "2026-06-19",
            right: "call",
            polygon_ticker: "O:MU260619C00190000",
            entry: 0.19,
            current_price: 0.34,
            bid: 0.32,
            ask: 0.36,
            volume: 18420,
            open_interest: 9210,
            entry_at: "2026-05-14T13:30:00Z",
            entry_quote_bid: 0.18,
            entry_quote_ask: 0.2,
            priced_at: "2026-05-15T20:00:00Z",
            price_source: "quote",
            is_stale: false,
          },
        ],
      },
      {
        id: "opt-nvda-call-spread",
        name: "NVDA 135/145 Call Debit Spread",
        kind: "option",
        ticker: "NVDA",
        underlying_ticker: "NVDA",
        direction: "long",
        strategy_type: "Call Debit Spread",
        expiry: "2026-06-19",
        days_to_expiry: 22,
        entry: 2.45,
        current_price: 3.18,
        pnl_pct: 29.8,
        batch_id: "run-2026-05-14T1330",
        registered_at: "2026-05-14T13:30:00Z",
        priced_at: "2026-05-15T20:00:00Z",
        is_stale: false,
        max_risk: 245,
        max_reward: 755,
        breakeven: 137.45,
        criteria: {
          "Call volume": "27,340",
          "Vol vs 20d avg": "3.1x",
          "Ask-side %": "64%",
          "OI change": "+58%",
          "IV rank": "29",
          "DTE": "22",
        },
        exit: { take_profit: 5.25, stop_loss: 1.35, time_exit_days: 10 },
        legs: [
          {
            kind: "option",
            ticker: "NVDA",
            underlying_ticker: "NVDA",
            action: "buy",
            quantity: 1,
            direction: "long",
            strike: 135,
            expiry: "2026-06-19",
            right: "call",
            polygon_ticker: "O:NVDA260619C00135000",
            entry: 5.8,
            current_price: 7.25,
            bid: 7.15,
            ask: 7.35,
            volume: 27340,
            open_interest: 18420,
            entry_at: "2026-05-14T13:30:00Z",
            priced_at: "2026-05-15T20:00:00Z",
            price_source: "quote",
            is_stale: false,
          },
          {
            kind: "option",
            ticker: "NVDA",
            underlying_ticker: "NVDA",
            action: "sell",
            quantity: 1,
            direction: "short",
            strike: 145,
            expiry: "2026-06-19",
            right: "call",
            polygon_ticker: "O:NVDA260619C00145000",
            entry: 3.35,
            current_price: 4.07,
            bid: 3.98,
            ask: 4.16,
            volume: 16480,
            open_interest: 11290,
            entry_at: "2026-05-14T13:30:00Z",
            priced_at: "2026-05-15T20:00:00Z",
            price_source: "daily",
            is_stale: false,
          },
        ],
      },
    ],
  },
  previous_trades: [
    {
      id: "prev-nvda-call-spread",
      kind: "option",
      ticker: "NVDA",
      underlying_ticker: "NVDA",
      name: "NVDA 135/145 Call Debit Spread",
      strategy_type: "Call Debit Spread",
      direction: "long",
      expiry: "2026-03-20",
      entry: 1.75,
      exit_price: 3.18,
      exit_rule: { take_profit: 3.25, stop_loss: 0.95, time_exit_days: 10 },
      exit_reason: "hit_target",
      pnl_pct: 81.7,
      pnl_usd: 817,
      fired_at: "2026-03-02T13:30:00Z",
      closed_at: "2026-03-19T20:00:00Z",
      criteria: {
        "Call volume": "31,200",
        "Vol vs 20d avg": "4.8x",
        "Ask-side %": "69%",
        "OI change": "+88%",
        "IV rank": "35",
        "DTE": "18",
      },
      legs: [
        {
          kind: "option",
          ticker: "NVDA",
          underlying_ticker: "NVDA",
          action: "buy",
          quantity: 1,
          direction: "long",
          strike: 135,
          expiry: "2026-03-20",
          right: "call",
          polygon_ticker: "O:NVDA260320C00135000",
          entry: 4.6,
          current_price: 7.2,
          exit_price: 7.2,
        },
        {
          kind: "option",
          ticker: "NVDA",
          underlying_ticker: "NVDA",
          action: "sell",
          quantity: 1,
          direction: "short",
          strike: 145,
          expiry: "2026-03-20",
          right: "call",
          polygon_ticker: "O:NVDA260320C00145000",
          entry: 2.85,
          current_price: 4.02,
          exit_price: 4.02,
        },
      ],
    },
    {
      id: "prev-spy-long-call",
      kind: "option",
      ticker: "SPY",
      underlying_ticker: "SPY",
      name: "SPY 600C Breakout Call",
      strategy_type: "Long Call",
      direction: "long",
      strike: 600,
      expiry: "2026-02-20",
      right: "call",
      polygon_ticker: "O:SPY260220C00600000",
      entry: 1.2,
      exit_price: 0.58,
      exit_rule: { take_profit: 3.0, stop_loss: 0.6, time_exit_days: 3 },
      exit_reason: "stopped",
      pnl_pct: -51.7,
      pnl_usd: -517,
      fired_at: "2026-02-09T13:30:00Z",
      closed_at: "2026-02-20T20:00:00Z",
      criteria: {
        "Call volume": "9,840",
        "Vol vs 20d avg": "2.2x",
        "Ask-side %": "58%",
        "OI change": "+31%",
        "IV rank": "22",
        "DTE": "11",
      },
      legs: [
        {
          kind: "option",
          ticker: "SPY",
          underlying_ticker: "SPY",
          action: "buy",
          quantity: 1,
          direction: "long",
          strike: 600,
          expiry: "2026-02-20",
          right: "call",
          polygon_ticker: "O:SPY260220C00600000",
          entry: 1.2,
          current_price: 0.58,
          exit_price: 0.58,
        },
      ],
    },
  ],
};

// ── Registry ────────────────────────────────────────────────────────────────

const MOCKS: Record<string, SignalPerformance> = {
  stock: EQUITY_SIGNAL,
  equity: EQUITY_SIGNAL,
  option: OPTION_SIGNAL,
};

export function getMockSignalPerformance(variant?: string | null): SignalPerformance {
  if (variant && MOCKS[variant]) return MOCKS[variant];
  return EQUITY_SIGNAL;
}
